import { type ComponentType, type ReactNode } from 'react';
import { KnowledgeAreaList } from '../components/knowledge-area';
import { LearningPathCarousel } from '@/components/partial/learning-path-carousel';
import { AiChip } from '@/components/shared/ai-chip';
import type { LearningTypeCardProps } from '@/components/shared/learning-type-card';
import { LearningTypeList } from '@/components/shared/learning-type-list';
import { TrendingChip } from '@/components/shared/trending-chip';
import type { HomeSectionKey } from '@/constants/analytics';
import type { KnowledgeArea } from '@/models/bootstrap/taxonomy';
import { CareerVideosSection } from '@/packages/home/components/career-videos-section';
import { CobrandedSection } from '@/packages/home/components/cobranded-section';
import { IaCourses } from '@/packages/home/components/ia-courses';
import { PlanCardSelector } from '@/packages/home/components/idp-section';
import { Recommendations } from '@/packages/home/components/recommendations';
import { UserProfile } from '@/packages/home/components/user-profile';
import { VideosSection } from '@/packages/home/components/videos-section';

interface HomeSectionsProps {
  knowledgeAreas: (KnowledgeArea & { link: string })[];
  courseTypes: LearningTypeCardProps[];
  idp?: boolean;
  einsteinCobrand?: boolean;
  learningPaths?: boolean;
  hasCompanyPaths?: boolean;
  companyName?: string;
  agentVideoRecommendation?: boolean;
}

export interface SectionConfig {
  id: number;
  key: HomeSectionKey;
  title?: string;
  description?: string;
  tag?: ComponentType;
  content: ReactNode;
  message?: string;
  skeletonType?: string;
  skipIaBuilder?: boolean;
}

const assignSequentialIds = (sections: SectionConfig[]): SectionConfig[] => {
  return sections.map((section, index) => ({
    ...section,
    id: index,
  }));
};

export const homeSections = ({
  knowledgeAreas,
  courseTypes,
  idp = true,
  einsteinCobrand = false,
  learningPaths = false,
  hasCompanyPaths = false,
  companyName = '',
  agentVideoRecommendation = false,
}: HomeSectionsProps) => {
  const baseSections: SectionConfig[] = [
    {
      id: 0,
      key: 'profile',
      skeletonType: 'profile',
      content: (
        <UserProfile
          agentVideoRecommendationActive={agentVideoRecommendation}
        />
      ),
      message: '👤 Analisando seu perfil...',
    },
    {
      id: 1,
      key: 'career_recommendations',
      title: 'Para aprofundar na sua carreira',
      description:
        'Cursos que aceleram o desenvolvimento das habilidades mais estratégicas da sua área e te impulsionam para a próxima etapa da sua trajetória profissional.',
      tag: AiChip,
      content: <Recommendations />,
      message: '🎯 Escolhendo cursos ideais para sua jornada...',
    },
    {
      id: 2,
      key: 'ai_courses',
      title: 'Domine a Inteligência Artificial',
      description:
        'A IA já está moldando o presente e vai definir o futuro. Estes cursos te ajudam a entender como ela funciona e como aplicá-la na prática na sua profissão',
      tag: TrendingChip,
      content: <IaCourses />,
      message: '🧠 Selecionando conteúdos de IA sob medida...',
    },
    {
      id: 3,
      key: 'ai_videos',
      title: 'Explorando Inteligência Artificial',
      description:
        'Do primeiro contato às aplicações mais avançadas: descubra como a IA está transformando o mundo.',
      tag: TrendingChip,
      content: <VideosSection />,
      message: '📹 Reunindo vídeos que podem te inspirar...',
    },
    {
      id: 4,
      key: 'career_videos',
      title: 'Habilidades para crescer na carreira',
      description:
        'Comunicação, produtividade, liderança e muito mais — vídeos essenciais para qualquer profissional evoluir.',
      tag: TrendingChip,
      content: <CareerVideosSection />,
      message: '📹 Reunindo vídeos que podem te inspirar...',
    },
  ];

  if (idp) {
    baseSections.splice(1, 0, {
      id: 5,
      key: 'idp',
      skeletonType: 'pp',
      title: 'Plano Personalizado',
      tag: AiChip,
      content: <PlanCardSelector />,
      message: '📋 Carregando plano de desenvolvimento...',
    });
  }

  if (einsteinCobrand) {
    baseSections.splice(idp ? 3 : 2, 0, {
      id: 6,
      key: 'einstein_cobrand',
      title: 'Faculdade Einstein + Galena',
      content: <CobrandedSection institutionId={383} />,
      skipIaBuilder: true,
    });
  }

  const conditionalSections: SectionConfig[] = [];

  if (hasCompanyPaths) {
    conditionalSections.push({
      id: 7,
      key: 'company_learning_paths',
      title: `Trilhas ${companyName || 'da Empresa'}`,
      description: 'Desenvolva as competências selecionadas pela sua empresa.',
      content: <LearningPathCarousel />,
      skipIaBuilder: true,
    });
  }
  if (learningPaths) {
    conditionalSections.push({
      id: 8,
      key: 'learning_paths',
      title: 'Roteiros de Aprendizagem',
      description:
        'Aprofunde-se nos assuntos que moldam o futuro do trabalho. Cada roteiro combina conteúdos, prática e reflexão para transformar aprendizado em resultado real.',
      content: <LearningPathCarousel global />,
      skipIaBuilder: true,
    });
  }

  const finalSections: SectionConfig[] = [
    ...baseSections,
    ...conditionalSections,
    {
      id: 9,
      key: 'course_types',
      title: 'Explore cursos por categoria',
      content: (
        <LearningTypeList
          data={courseTypes.map((courseType) => ({
            ...courseType,
            analyticsItem: { type: 'course_type', id: courseType.label },
          }))}
        />
      ),
      skipIaBuilder: true,
    },
    {
      id: 10,
      key: 'knowledge_areas',
      title: 'Explore cursos por tema',
      content: <KnowledgeAreaList data={knowledgeAreas} />,
      skipIaBuilder: true,
    },
  ];

  return assignSequentialIds(finalSections);
};
